import { FC, useEffect, useMemo, useState } from 'react'
import { Box, Center, Flex, HStack, useRadioGroup, VStack, Spinner, Image, useBreakpointValue } from '@chakra-ui/react'
import { RadioButtonProps } from './interfaces'
import { SystemProps } from '@chakra-ui/system'
import { DefaultCheckbox } from '../checkboxes/default-checkbox'
import { addAnswer, selectAnswers, selectCurrentStep, selectScenarioType } from '../../state'
import { cardImageMap, ScenarioType, Steps } from '../../state/interfaces'
import { useDispatch, useSelector } from 'react-redux'
import { selectScenarioIsWaitingForAnswer } from '../../state'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'

interface Choice {
  value: string
  label: string
  image?: string
  imageSelected?: string
  isChecked?: boolean
  metadata?: any
}

export interface RadioButtonGroupWithToggleProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
  justify?: SystemProps['justifyContent']
  direction?: 'horizontal' | 'vertical'
  options?: any
  grandImage?: string
}

export const RadioButtonGroupWithToggle: FC<RadioButtonGroupWithToggleProps> = ({
  choices,
  value,
  onChange,
  name,
  component: Component,
  direction = 'horizontal',
  options,
  grandImage,
}) => {
  const dispatch = useDispatch()
  const currentStep = useSelector(selectCurrentStep)
  const scenarioType = useSelector(selectScenarioType)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => {
      const timeOut = currentStep && currentStep.id === Steps.DECISION_MOBILE_INTERNET ? 350 : 0
      if (currentStep && currentStep.id === Steps.HOW_MANY_CALLING_MINUTES) {
        if (options) {
          dispatch(addAnswer({ stepId: Steps.CALLING_OPTIONS, answerIds: selectedChoices, hidden: true }))
        }
      } else if (currentStep && currentStep.id === Steps.HOW_MUCH_DATA) {
        if (options) {
          dispatch(addAnswer({ stepId: Steps.DATA_OPTIONS, answerIds: selectedChoices, hidden: true }))
        }
      }
      setTimeout(() => {
        return onChange && onChange([value])
      }, timeOut)
    },
    value,
    name,
  })
  const Stack = useMemo<typeof HStack | typeof VStack>(
    () => (direction === 'horizontal' ? HStack : VStack),
    [direction],
  )
  const isDesktop = useBreakpointValue({ base: false, lg: true })
  const [selectedImage, setSelectedImage] = useState('')
  const handleImageClick = (image: string) => {
    setSelectedImage(image)
  }
  const [selectedChoices, setSelectedChoices] = useState<string[]>([])
  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedChoices((prevSelected) => [...prevSelected, value])
    } else {
      setSelectedChoices((prevSelected) => prevSelected.filter((val) => val !== value))
    }
  }
  const stepsWithFalseButton = [Steps.START_RATING, Steps.THANKS, Steps.THANKS_COUNTRY]
  const isStepwithFalseButton = currentStep ? stepsWithFalseButton.includes(currentStep.id as Steps) : false

  useEffect(() => {
    if (options) {
      options.forEach((option: any) => {
        if (option.isChecked) {
          setSelectedChoices((prevSelected) => [...prevSelected, option.value])
        }
      })
    }
  }, [])

  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const answers = useSelector(selectAnswers)
  const isScenarioVisitor = scenarioType === ScenarioType.CANADA_VISITOR
  const isLastStepFromVisitorScenario = currentStep && currentStep.id === Steps.THANKS && isScenarioVisitor

  return (
    <VStack>
      {(answers &&
        answers['buy-or-bring-phone-quick-search'] &&
        answers['buy-or-bring-phone-quick-search'].includes('finishScenario')) ||
      isLastStepFromVisitorScenario ? (
        isScenarioVisitor ? (
          <Box marginTop={6}>
            <Spinner />
          </Box>
        ) : (
          <Spinner />
        )
      ) : (
        <>
          <Center>
            {options && (
              <VStack>
                {currentStep?.id === Steps.HOW_MUCH_DATA ? (
                  <Flex direction="column" marginTop={5} marginBottom={1}>
                    <Flex justifyContent="center">
                      {options
                        .filter((option: any) => option.group === 'technology')
                        .map((option: any) => {
                          return (
                            <HStack key={option.value} height={50} alignItems="center">
                              <DefaultCheckbox
                                choice={option}
                                isChecked={option.isChecked}
                                toggle={true}
                                onChange={handleCheckboxChange}
                                type="data"
                              />
                            </HStack>
                          )
                        })}
                    </Flex>
                  </Flex>
                ) : (
                  <Flex direction="column" marginTop={5} marginBottom={2}>
                    <Flex direction="column" justifyContent="center">
                      {options
                        .filter((option: any) => option.group === 'options')
                        .map((option: any) => (
                          <Box key={option.value} mb={4}>
                            <DefaultCheckbox
                              key={option.value}
                              choice={option}
                              isChecked={option.isChecked}
                              onChange={handleCheckboxChange}
                            />
                          </Box>
                        ))}
                    </Flex>
                  </Flex>
                )}
              </VStack>
            )}
          </Center>
          <Center>
            <Stack>
              <Flex alignItems="center">
                {isDesktop && image && !isStepwithFalseButton && (
                  <Box marginRight={1}>
                    <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Image src={image} alt="image" width="80%" />
                    </div>
                  </Box>
                )}
                <VStack>
                  <Stack
                    {...getRootProps()}
                    width={isDesktop && image ? '50%' : '100%'}
                    justifyContent="center"
                    alignItems="center"
                    spacing={[0, 2]}
                    flexWrap="wrap"
                  >
                    {choices.map((props) =>
                      props.image ? (
                        <div key={`choice-${props.value}`} onClick={() => handleImageClick(props.imageSelected || '')}>
                          <Component
                            {...getRadioProps({ value: props.value })}
                            {...props}
                            image={selectedImage === props.imageSelected ? props.imageSelected : props.image}
                          />
                        </div>
                      ) : scenarioIsWaitingForAnswer ? (
                        <LoadingNewAnswer key={`choice-${props.value}`} />
                      ) : (
                        <Component
                          key={`choice-${props.value}`}
                          {...getRadioProps({ value: props.value })}
                          {...props}
                        />
                      ),
                    )}
                  </Stack>
                  {image && isStepwithFalseButton && (
                    <Box marginRight={1}>
                      <div style={{ flex: '60%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Image src={image} alt="image" width={'60%'} height={'60%'} />
                      </div>
                    </Box>
                  )}
                </VStack>
              </Flex>
              <Flex direction="column" alignItems="center" justifyContent="center">
                {/* Other components */}
                {!isDesktop && image && !isStepwithFalseButton && (
                  <Center>
                    <Image src={image} alt="image" width="80%" />
                  </Center>
                )}
              </Flex>
            </Stack>
          </Center>
        </>
      )}
    </VStack>
  )
}
